/**
 * Auth context — cookie-session based.
 *
 * After login, the backend issues an HttpOnly `bp_session` cookie. The browser
 * automatically attaches it to every same-origin request through axios's
 * `withCredentials: true`. We don't store the JWT in localStorage anywhere —
 * that means an XSS attacker can't lift the credential.
 *
 * What we *do* keep in localStorage is a non-sensitive UI cache of the user
 * record (`bp:auth:user`) so the app shell can render their name / role
 * instantly on reload without a flash of "Signing in...". The cookie is the
 * source of truth: every mount calls `/api/auth/me` to validate it.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { logger } from "./logger";

const USER_CACHE_KEY = "bp:auth:user";

function readCachedUser() {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    logger.debug("auth:read-cache", e);
    return null;
  }
}

function writeCachedUser(user) {
  try {
    if (user) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_CACHE_KEY);
  } catch (e) {
    logger.debug("auth:write-cache", e);
  }
}

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  // Optimistic seed from cache so the shell can paint immediately. Truth is
  // confirmed by the /me call below.
  const [user, setUser] = useState(() => {
    const cached = readCachedUser();
    return cached && cached._user_type === "staff" ? cached : null;
  });
  const [customer, setCustomer] = useState(() => {
    const cached = readCachedUser();
    return cached && cached._user_type !== "staff" ? cached : null;
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      writeCachedUser(data);
      if (data._user_type === "staff") {
        setUser(data);
        setCustomer(null);
      } else {
        setCustomer(data);
        setUser(null);
      }
    } catch (e) {
      // 401 just means "no valid cookie" — that's the unauthenticated state,
      // not an error worth surfacing.
      if (e?.response?.status !== 401) {
        logger.warn("auth:refresh", e);
      }
      writeCachedUser(null);
      setUser(null);
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loginStaff = async (email, password) => {
    const { data } = await api.post("/auth/staff/login", { email, password });
    // Cookie is set by the response. Just refresh state from /me so we get
    // the canonical user record (including effective permissions).
    await refresh();
    return data.user;
  };

  const loginCustomer = async (email, password) => {
    const { data } = await api.post("/auth/customer/login", { email, password });
    await refresh();
    return data.customer;
  };

  const registerCustomer = async (payload) => {
    const { data } = await api.post("/auth/customer/register", payload);
    await refresh();
    return data.customer;
  };

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      logger.debug("auth:logout-network", e);
    }
    writeCachedUser(null);
    setUser(null);
    setCustomer(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user, customer, loading, refresh,
        loginStaff, loginCustomer, registerCustomer, logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
